import _ from 'lodash';

import { getSelfRegInstallerId, iterateOverResolutionsAndModes, SELF_REG_INSTALLER_IDS } from '../utils/pricing';
import { getFieldValue } from '../utils/maths';
import { getCloudAdapterOrder } from '../utils/cloudAdapters';
import { getDisplayedCloudDays } from '../utils/plan';

import { getAuthToken, getInstallerId, getUid } from './managers/store';
import { makePrivateRequestAsUser, makePublicRequest } from './managers/requestBuilder';
import { getUserLocation } from './user';
import { getCloudAdapterPrices } from './pricing';

// Set `hasInstaller` to true if user is not logged in (so getInstallerId() will be undefined) but we know user has an installer
// In all other cases this argument can be left undefined
export const getPlanInfo = async (hasInstaller) => {
    // /paymentinfo only works if user has an installer
    // Otherwise we use old /account-levels
    if (hasInstaller || getInstallerId()) {
        const { data } = await makePublicRequest(
            'payment',
            'GET',
            '/paymentinfo',
            {
                params: {
                    uid: getUid(),
                    token: getAuthToken(),
                },
            }
        );

        const cloudStorageDays = data.viewer_duration / (60 * 60 * 1000 * 24);
        const isEUP = /^pro_/.test(data.provider);

        return {
            // EUP only
            isEUP,
            isEUPPending: data.provider === 'pro_pending',
            isEUPActive: data.provider === 'pro_stripe',
            noEUPPlanChosen:
                isEUP && (data.maxdevices === null || data.maxdevices === 0),
            canChangePlan: !!data.allow_user_plan_change,
            cancelSubscriptionDate: data.cancellation_date && new Date(data.cancellation_date),

            // VL self reg
            isSelfReg: _.values(SELF_REG_INSTALLER_IDS).includes(data.id),

            // All
            isOnFreePlan: data.cancelled,
            suspended: data.suspended,
            maxDevices: data.maxdevices,
            devicesQuota: data.devices_quota,
            // TODO: check for any lingering analytics_devices
            addons: data.analytics_devices,
            cloudStorageDays: getDisplayedCloudDays(cloudStorageDays),
            currency: data.currency,
            countryCode: data.country_code,
            price: data.pro_price, // Includes tax
            annualPaymentFrequency: data.sub_renewal_frequency_months === 12,
            nextPayment:
                data.pro_subscription_expiry &&
                new Date(data.pro_subscription_expiry),
            company: data.company,
            phone: data.phone,
            email: data.installer_email,
            logo: data.logo,
            accountLevel: data.accountlevel
        };
    } else {
        const res = await makePublicRequest(
            'payment',
            'GET',
            '/account-levels',
            {
                params: {
                    uid: getUid(),
                    token: getAuthToken(),
                    annual: 1,
                },
            }
        );

        const accountLevel = res.data.plans.filter(
            (plan) => plan.account_level === res.data.accountlevel
        )[0];

        const nextAccountLevel = res.data.accountChangeDate && res.data.nextAccountLevel && res.data.plans.filter(
            (plan) => plan.account_level === res.data.nextAccountLevel
        )[0];

        return {
            isEUP: false,
            currency: res.data.currency,
            maxDevices: accountLevel?.maxdevices,
            cloudStorageDays: accountLevel?.length,
            provider: res.data.provider,
            cancelSubscriptionDate: nextAccountLevel?.monthly_fee === 0 && new Date(res.data.accountChangeDate)
        };
    }
};

export const getSitesInfo =  makePrivateRequestAsUser(async ({ makePrivateRequest }) => {
        const { data } = await makePrivateRequest(
            'auth',
            'GET',
            '/account/:uid/selfreg/sites'
        );
        return data;
    }    
);

// Cancels subscription
export const cancelPlan = makePrivateRequestAsUser(async ({ makePrivateRequest }, reason) => {
    return makePrivateRequest(
        'payment',
        'POST',
        '/account/:uid/plan/cancel',
        {
            data: {
                reason
            }
        }
    );
});

export const changePlan = makePrivateRequestAsUser(async (
    { makePrivateRequest },
    accountLevel,
    cameraQuota,
    analyticsQuota,
    paymentInterval,
    isRegistration,
    quoteId
) => {
    let reqData = {
        accountlevel: accountLevel,
        cameraquota: cameraQuota,
        analyticsQuota: analyticsQuota,
        renewalfrequency: paymentInterval === 'annually' ? 12 : 1,
        uiflow: isRegistration ? 'registration' : undefined, // Needed so backend sends welcome email
    };
    if (quoteId) {
        reqData = {
            ...reqData,
            quoteId: quoteId
        };
    }
    return makePrivateRequest(
        'payment',
        'PUT',
        '/account/:uid/plan',
        {
            data: {...reqData},
        }
    );
});

const parseSitesFromPlan = (plan) => {
    const sites = {
        first: {...plan.first}, 
        ...(plan.extraSites ? Object.fromEntries(
            Object.entries(
                plan.extraSites
            ).map(([key, value]) => [key, { ...value.fieldsProps }])
        ) : {})
    };
    const sitesInfo = [];
    Object.values(sites).forEach(
        site => {
            const newSite = {};
            iterateOverResolutionsAndModes((resolution, recordingMode) => {
                if (parseInt(getFieldValue(site[`cameras${resolution}_${recordingMode}`])) > 0) {
                    if (newSite[resolution]) {
                        newSite[resolution] = {
                            ...newSite[resolution],
                            [recordingMode]: parseInt(getFieldValue(site[`cameras${resolution}_${recordingMode}`]))
                        }
                    } else {
                        newSite[resolution]={
                            [recordingMode]: parseInt(getFieldValue(site[`cameras${resolution}_${recordingMode}`]))
                        };
                    }
                }
            });
            sitesInfo.push(newSite);
        }
    );
    return sitesInfo;
};

export const saveSitesInfo = makePrivateRequestAsUser(async (
    {makePrivateRequest},
    plan
) => {
   const sitesInfo = parseSitesFromPlan(plan);

    return makePrivateRequest(
        'auth',
        'PUT',
        '/account/:uid/selfreg/sites',
        {
            data: {
                sites: sitesInfo
            }
        }
    );
});

/**
 * Reinstate the user's subscription which must be currently pending cancellation.
 * @returns axios promise
 */
export const reinstateSubscription = makePrivateRequestAsUser(async ({ makePrivateRequest }) => {
    return makePrivateRequest('payment', 'POST', '/account/:uid/plan/reverse-cancellation');
});

// Check if there is an upgrade fee when changing plan
// Upgrade fee is charged when moving to a more expensive plan
export const calculateUpgradeFee = makePrivateRequestAsUser(async(
    { makePrivateRequest },
    accountLevel,
    cameraQuota,
    analyticsQuota
) => {
    const { data } = await makePrivateRequest(
        'payment',
        'POST',
        '/account/:uid/plan/upgradefee',
        {
            data: {
                accountlevel: accountLevel,
                cameraquota: cameraQuota,
                analyticsQuota: analyticsQuota
            },
        }
    );

    return parseFloat(data.fee);
});

export const updateShippingAddress = makePrivateRequestAsUser(async (
    { makePrivateRequest },
    name,
    street,
    city,
    region,
    postcode,
    country,
    phone
) => {
    const { data } = await makePrivateRequest(
        'payment',
        'PUT',
        '/account/:uid/shipping-address',
        {
            data: {
                shipping: {
                    name,
                    street,
                    city,
                    region: region || undefined, // Region not required but cannot pass empty string
                    postcode,
                    country,
                    phone,
                },
            },
        }
    );

    return data;
});

export const getShippingAddress = makePrivateRequestAsUser(async ({ makePrivateRequest }) => {
    const { data } = await makePrivateRequest(
        'payment',
        'GET',
        '/account/:uid/shipping-address'
    );

    return _.pick(data, [
        'name',
        'street',
        'city',
        'region',
        'postcode',
        'country',
        'phone',
    ]);
});

export const emailQuoteTo = async (emailAddress, plan, pilot) => {
    let params;
    const uid =  getUid();
    const userLocation = await getUserLocation();
    const countryCode = userLocation.country_code;
    // If no uid, then this is a non-logged in user so use Videoloft installer
    // Otherwise user is logged in so pass uid
    if (!uid) {
        params = {
            installerId: getSelfRegInstallerId(userLocation),
            countryCode: countryCode,
        };
    } else {
        params = {
            userId: uid,
        };
    }
    const sites = parseSitesFromPlan(plan);

    const caPricing = await getCloudAdapterPrices(countryCode);
    const cloudAdapterOrder = getCloudAdapterOrder(plan.cloudAdapters, caPricing);

    const cloudStorageDays = plan.cloudStorageDays === 365 ? 366 : plan.cloudStorageDays === 730 ? 731 : plan.cloudStorageDays;

    const reqBody = {
        data: {
            email: emailAddress,
            plan: {
                sites: sites,
                products: Object.keys(cloudAdapterOrder).map(id => ({
                    id: id,
                    quantity: cloudAdapterOrder[id]
                })),
                ...(plan.addons && Object.keys(plan.addons).length > 0 ? { addons: plan.addons } : {}),
                renewalFrequency: plan.paymentInterval === 'anually' ? 12 : 1,
                cloudStorage: cloudStorageDays
            },
            pilot
        }
    };

    await makePublicRequest(
        'payment',
        'POST',
        '/quote',
        {
            ...reqBody,
            params: params
        }
    );
};

export const getQuote = async (id, invalidQuoteCb) => {
    try {
        const { data } = await makePublicRequest(
            'payment',
            'GET',
            `/quote/${id}`
        );
        return data;
    } catch (err) {
        // quote doesn't exist/expired
        invalidQuoteCb();
    }
};